import i18n from 'i18next';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import { FORMAT_FILTER_IDS, PRICE_FILTER_IDS } from '../common/base-search-result/base-search-result.constants';
import detectumConfig, { DETECTUM_SERVICES } from '../constants/detectum-config';
import { world } from '../languages';
import { AVAILABLE_WORLDS } from '../languages/constants';
import isAuthorDomain from './is-author-domain';

enum DETECTUM_PARAMS {
    AUTHOR = 'author',
    CATEGORY = 'category',
    FILE_TYPES = 'fileTypes',
    IS_DOWNLOADABLE = 'is_downloadable',
    IS_FREE = 'isFree',
    IS_INTERACTIVE = 'is_interactive',
    IS_NOT_DOWNLOADABLE = 'isStandaloneInteractive',
    IS_SALE = 'is_sale',
    JAHRGANGSSTUFE = 'Jahrgangsstufe',
    LANGUAGE = 'language',
    LEARNING_YEARS = 'learningYears',
    MATERIALTYP = 'Materialtyp',
    PRICE_RANGE = 'price_range',
    SCHOOL_TYPES = 'schoolTypes',
    TAGS = 'tags',
    TOTAL_PAGES = 'totalPages'
}

enum KEY {
    AUTHOR = 'author',
    C = 'c',
    F = 'f',
    FT = 'ft',
    LNG = 'lng',
    LY = 'ly',
    MT = 'mt',
    PRC = 'prc',
    SALE = 'sale',
    ST = 'st',
    T = 't',
    TG = 'tg',
    TP = 'tp'
}

export type DetectumSerializedSearchParams = Partial<SearchState> & {
    page?: number;
};

export type DetectumNativeParams = {
    category?: number;
    limit?: number;
    offset?: number;
    order?: string;
    q?: string;
};

type DetectumFilter = {
    children: DetectumFilter[];
    count?: number;
    doc_count?: number;
    id?: string | number;
    title?: string;
    total?: number;
    value?: string;
};

type DetectumMaterial = Material & {
    materialFiles_types: string[];
};

export type DetectumFormatterResponse = {
    error: unknown;
    // eslint-disable-next-line
    results: { filters: any; items: DetectumMaterial[]; total: number };
};

const s = {
    'authorFeatured-desc': 'authorfeatured_desc',
    'createdAt-desc': 'created_at_desc',
    'price-asc': 'price_asc',
    'price-desc': 'price_desc'
};

const interactiveWorlds = {
    [AVAILABLE_WORLDS.DE]: {
        hybrid: 'Downloads & Interactive',
        onlyInteractive: 'Nur interactive'
    },
    [AVAILABLE_WORLDS.ES]: {
        hybrid: 'Descargas e interactivo',
        onlyInteractive: 'Solo interactivo'
    },
    [AVAILABLE_WORLDS.FR]: {
        hybrid: 'Téléchargements et interactif',
        onlyInteractive: 'Uniquement interactif'
    },
    [AVAILABLE_WORLDS.IT]: {
        hybrid: 'Download e interattivo',
        onlyInteractive: 'Solo interattivo'
    },
    [AVAILABLE_WORLDS.GR]: {
        hybrid: 'Λήψεις & Διαδραστικά',
        onlyInteractive: 'Μόνο διαδραστικό'
    }
};

const detectumHelper = {
    adaptMaterials: (materials: DetectumMaterial[]) => {
        materials.forEach((item) => {
            if (item.materialFiles_types?.length) {
                // @ts-ignore
                item.materialFiles = item.materialFiles_types.map((item) => ({
                    type: item
                }));
            }
        });

        return materials;
    },

    adaptResponse(
        response: DetectumFormatterResponse,
        search: DetectumSerializedSearchParams
    ): MaterialsSearchResolveDataType {
        let taxonomyParents = [];
        let taxonomyFilters = [];
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const formatFilters = { values: [] };

        if (response.error || !response.results.total) {
            return {
                code: 2,
                data: {
                    items: {},
                    total: 0
                },
                errors: []
            } as unknown as MaterialsSearchResolveDataType;
        }

        const { filters } = response.results;

        const classFilters = find(filters, {
            name: DETECTUM_PARAMS.JAHRGANGSSTUFE
        }) || { values: [] };

        classFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;

            delete item.value;
            delete item.count;
        });

        const first = classFilters.values.filter((c: { id: string }) => Number(c.id) === 17);

        const second = classFilters.values.filter((c: { id: string }) => Number(c.id) !== 17);

        classFilters.values = [...first, ...sortBy(second, (c: { id: string }) => Number(c.id))];

        const materialTypeFilters = find(filters, {
            name: DETECTUM_PARAMS.MATERIALTYP
        }) || { values: [] };

        materialTypeFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const sale = find(filters, {
            name: DETECTUM_PARAMS.IS_SALE
        }) || { values: [] };

        sale.values.forEach((item: DetectumFilter) => {
            item.title = 'SALE';
            item.id = 1;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const fileTypeFilters = find(filters, {
            name: DETECTUM_PARAMS.FILE_TYPES
        }) || { values: [] };

        fileTypeFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;
            item.id = item.value;

            delete item.count;
            delete item.value;
        });

        const totalPagesFilters = find(filters, {
            name: DETECTUM_PARAMS.TOTAL_PAGES
        }) || { values: [] };

        totalPagesFilters.values = totalPagesFilters.values.map((item: DetectumFilter) => ({
            id: item.id,
            title: item.value,
            total: item.count
        }));

        const priceFilters = find(filters, {
            name: DETECTUM_PARAMS.PRICE_RANGE
        }) || { values: [] };

        priceFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const freeFilters = find(filters, {
            name: DETECTUM_PARAMS.IS_FREE
        }) || { values: [] };

        priceFilters.values.unshift({
            id: 3,
            title: i18n.t('common:common.materialBlock.free'),
            total: freeFilters.values.find((item: DetectumFilter) => item.value === 'True')?.count || 0
        });

        const schoolFilters = find(filters, {
            name: DETECTUM_PARAMS.SCHOOL_TYPES
        }) || { values: [] };

        schoolFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const languageFilters = find(filters, {
            name: DETECTUM_PARAMS.LANGUAGE
        }) || { values: [] };

        languageFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const lyFilters = find(filters, {
            name: DETECTUM_PARAMS.LEARNING_YEARS
        }) || { values: [] };

        lyFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const tags = find(filters, {
            name: DETECTUM_PARAMS.TAGS
        }) || { values: [] };

        tags.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.id = item.value;
            item.total = item.count;

            delete item.count;
            delete item.value;
        });

        const interactive = find(filters, {
            name: DETECTUM_PARAMS.IS_INTERACTIVE
        }) || { values: [] };

        interactive.values.forEach((item: DetectumFilter) => {
            if (Number(item.id) === 1) {
                item.title = i18n.t('search:search.filters.interactive');
                item.id = FORMAT_FILTER_IDS.INTERACTIVE;
                item.total = item.count;

                delete item.count;
                delete item.value;

                // @ts-ignore
                formatFilters.values.push(item);
            }
        });

        const downloadable = find(filters, {
            name: DETECTUM_PARAMS.IS_DOWNLOADABLE
        }) || { values: [] };

        downloadable.values.forEach((item: DetectumFilter) => {
            if (Number(item.id) === 1) {
                item.title = i18n.t('search:search.filters.download');
                item.id = FORMAT_FILTER_IDS.DOWNLOADABLE;
                item.total = item.count;

                delete item.count;
                delete item.value;

                // @ts-ignore
                formatFilters.values.push(item);
            }
        });

        const notDownloadable = find(filters, {
            name: DETECTUM_PARAMS.IS_NOT_DOWNLOADABLE
        }) || { values: [] };

        notDownloadable.values.forEach((item: DetectumFilter) => {
            if (Number(item.id) === 1) {
                item.title = interactiveWorlds[world].onlyInteractive;
                item.id = FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE;
                item.total = item.count;

                delete item.count;
                delete item.value;

                // @ts-ignore
                formatFilters.values.push(item);
            }
            if (Number(item.id) === 0) {
                item.title = interactiveWorlds[world].hybrid;
                item.id = FORMAT_FILTER_IDS.INTERACTIVE_HYBRID;
                item.total = item.count;

                delete item.count;
                delete item.value;

                // @ts-ignore
                formatFilters.values.push(item);
            }
        });

        // if (priceFilters.values.length) {
        //     const i1 = findLastIndex(priceFilters.values, {
        //         title: '<1€'
        //     });
        //
        //     if (i1 > 0) {
        //         const f1 = priceFilters.values.splice(i1, 1);
        //         priceFilters.values.unshift(f1[0]);
        //
        //     }
        //     const i2 = findLastIndex(priceFilters.values, {
        //         title: 'nur kostenlos'
        //     });
        //
        //     if (i2 > 0) {
        //         const f2 = priceFilters.values.splice(i2, 1);
        //         priceFilters.values.unshift(f2[0]);
        //     }
        // }

        taxonomyFilters =
            find(filters, {
                name: DETECTUM_PARAMS.CATEGORY
            }) || [];

        if (taxonomyFilters.values.length) {
            taxonomyFilters.values = taxonomyFilters.values.filter(
                (filter: { value: string }) => filter.value !== 'Meta'
            );
        } else {
            taxonomyFilters.values = [taxonomyFilters.values];
        }

        taxonomyFilters.values.forEach((item: DetectumFilter) => {
            item.title = item.value;
            item.total = item.doc_count;

            if (Array.isArray(item?.children)) {
                item?.children?.forEach((i) => {
                    i.title = i.value;
                    i.total = i.doc_count;

                    delete i.count;
                    delete i.doc_count;
                });
            }

            delete item.count;
            delete item.doc_count;
        });

        if (search.t) {
            // @ts-ignore
            taxonomyParents = taxonomyFilters.values[0].parents.map((p) => {
                p.title = p.value;
                p.total = p.doc_count;

                delete p.count;
                delete p.doc_count;

                return p;
            });
        }

        // const cur = response.results.current_category;
        //
        // if ((`${cur[0].value }`) !== '0') {
        //     let children = response.results.categories.filter((item) => {
        //         return item.parents[item.parents.length - 1].id === cur[0].value;
        //     });
        //
        //     children = children.map((item) => {
        //         return {
        //             id: item.value,
        //             title: item.name,
        //             total: item.count
        //         };
        //     });
        //
        //     taxonomyFilters = [{
        //         id: cur[0].value,
        //         title: cur[0].name,
        //         children
        //     }];
        //
        //     taxonomyParents = [{
        //         id: null,
        //         title: 'Alle Fächer'
        //     }];
        //
        //     let part = cur[0].parents.slice(2);
        //
        //     part = part.map(part, (item) => {
        //         return {
        //             id: item.id,
        //             title: item.name
        //         };
        //     });
        //
        //     taxonomyParents = taxonomyParents.concat(part);
        // } else {
        //     taxonomyParents = [];
        //     taxonomyFilters = response.results.categories.filter((item) => {
        //         return item.parents[item.parents.length - 1].id === '0';
        //     });
        //
        //     taxonomyFilters = taxonomyFilters.map((item) => {
        //         return {
        //             id: item.value,
        //             title: item.name,
        //             total: item.count
        //         };
        //     });
        // }

        return {
            code: response.results.total === 0 ? 2 : '',
            data: {
                items: {
                    autoSuggest: { autoAppliedFilters: {} } as AutoSuggestType,
                    filters: {
                        class_grade: classFilters.values,
                        learning_years: lyFilters.values,
                        school_types: schoolFilters.values,
                        material_type: materialTypeFilters.values,
                        format: formatFilters.values,
                        file_types: fileTypeFilters.values,
                        languages: languageFilters.values,
                        price: priceFilters.values,
                        price_sale: sale.values,
                        tags: tags.values,
                        total_pages: totalPagesFilters.values
                    },
                    materials: self.adaptMaterials(response.results.items),
                    taxonomyFilters: taxonomyFilters.values,
                    taxonomyParents
                },
                total: response.results.total
            }
        };
    },

    defineDetectumUrl: (type: DETECTUM_SERVICES) => {
        const { href } = window.location;

        if (href.indexOf('eduki.com') >= 0 || isAuthorDomain()) {
            return detectumConfig.production[type][world];
        }

        return detectumConfig.staging[type][world];
    },

    processSearchParams(searchParams: DetectumSerializedSearchParams) {
        const params = {
            author: [searchParams.author] || [],
            c: searchParams.c || [],
            f: searchParams.f || [],
            ft: searchParams.ft || [],
            lng: searchParams.lng || [],
            ly: searchParams.ly || [],
            mt: searchParams.mt || [],
            prc: searchParams.prc || [],
            sale: searchParams.sale || [],
            st: searchParams.st || [],
            tg: searchParams.tg || [],
            tp: searchParams.tp || []
        };
        const detectumParams: DetectumNativeParams = {};
        const keys = Object.keys(params);

        let paramsNumber = 0;

        keys.forEach((key: string) => {
            params[key].forEach((id: string) => {
                paramsNumber++;
                console.info(params, key);
                const filter: { title?: string } = {};

                if (key === KEY.MT) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.MATERIALTYP;
                    filter.title = id;
                } else if (key === KEY.C) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.JAHRGANGSSTUFE;
                    filter.title = id;
                } else if (key === KEY.F) {
                    const isInteractive =
                        params[key].includes(`${FORMAT_FILTER_IDS.INTERACTIVE}`) ||
                        params[key].includes(`${FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE}`);

                    const neutralizingSelection =
                        isInteractive && params[key].includes(`${FORMAT_FILTER_IDS.DOWNLOADABLE}`);

                    const allInteractives =
                        params[key].includes(`${FORMAT_FILTER_IDS.INTERACTIVE_HYBRID}`) &&
                        params[key].includes(`${FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE}`);

                    if (!neutralizingSelection) {
                        if (Number(id) === FORMAT_FILTER_IDS.INTERACTIVE) {
                            detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.IS_INTERACTIVE;
                            filter.title = '1';
                        }

                        if (Number(id) === FORMAT_FILTER_IDS.DOWNLOADABLE) {
                            detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.IS_DOWNLOADABLE;
                            filter.title = '1';
                        }

                        if (!allInteractives && isInteractive) {
                            if (Number(id) === FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE) {
                                detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.IS_NOT_DOWNLOADABLE;
                                filter.title = '1';
                            }

                            if (Number(id) === FORMAT_FILTER_IDS.INTERACTIVE_HYBRID) {
                                detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.IS_NOT_DOWNLOADABLE;
                                filter.title = '0';
                            }
                        }
                    }
                } else if (key === KEY.LNG) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.LANGUAGE;
                    filter.title = id;
                } else if (key === KEY.PRC) {
                    if (Number(id) === PRICE_FILTER_IDS.FREE) {
                        detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.IS_FREE;
                        filter.title = 'true';
                    } else {
                        detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.PRICE_RANGE;
                        filter.title = id;
                    }
                } else if (key === KEY.FT) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.FILE_TYPES;
                    filter.title = id;
                } else if (key === KEY.TP) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.TOTAL_PAGES;
                    filter.title = id;
                } else if (key === KEY.T) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.CATEGORY;
                    filter.title = id;
                }
                if (key === KEY.TG) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.TAGS;
                    filter.title = id;
                } else if (key === KEY.SALE) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.IS_SALE;
                    filter.title = id;
                } else if (key === KEY.ST) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.SCHOOL_TYPES;
                    filter.title = id;
                } else if (key === KEY.LY) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.LEARNING_YEARS;
                    filter.title = id;
                } else if (key === KEY.AUTHOR) {
                    detectumParams[`param_${paramsNumber}`] = DETECTUM_PARAMS.AUTHOR;
                    filter.title = id;
                }

                detectumParams[`id_${paramsNumber}`] = filter.title;
            });
        });

        if (searchParams.t) {
            // Changed to just &category=searchParams.t on 17.12.2020 in contexts of fix of detectum bug
            // detectumParams[`param_${ ++paramsNumber}`] = 'category';
            // detectumParams[`id_${ paramsNumber}`] = searchParams.t;
            detectumParams.category = searchParams.t;
        }

        detectumParams.order = searchParams.s ? s[searchParams.s] : '';
        detectumParams.limit = searchParams.limit || 36;
        detectumParams.offset =
            (((searchParams.p as number) || (searchParams.page as number) || 1) - 1) * (detectumParams.limit as number);
        detectumParams.q = searchParams.query;

        if (!detectumParams.order && !detectumParams.q) {
            detectumParams.order = '';
        }

        return detectumParams;
    }
};

export default detectumHelper;
